/**
 * RFQ PDF Export (Solicitud de Cotización)
 *
 * Generates a branded, price-less "Solicitud de Cotización" PDF from an rfq
 * object, using ProMedic's official palette:
 *  - Azul Médico primario #0057B8 (rgb 0,87,184) — brand color.
 *  - Naranja Industrial #F7931E (rgb 247,147,30) — accent (líneas/detalles).
 *  - Gris claro #f8f9fa for the info blocks / alternating rows.
 *
 * Layout:
 *  - Blue header band (~38mm) with the WHITE logo embedded (loaded async from
 *    /images/logos/Logo_PROMEDIC_Blanco-07.png; falls back to "ProMedic Group"
 *    text if the image fails to load), company info under it, and the title +
 *    RFQ number / date on the right. An orange accent line sits under the band.
 *  - Cliente / Proyecto info blocks on a light-gray background.
 *  - Items grouped by `alcance`. Columns:
 *    Producto | Descripción | Cant. | Unidad | Precio est. unit. | Subtotal —
 *    the two price columns are printed EMPTY for the store to fill in
 *    (estimated, since this is a quote request — the store completes them).
 *    Producto = item.name (fallback to description); Descripción = item.description.
 *  - Items with an unknown quantity are left blank in the Cant. column.
 *  - "Total general" with an orange accent line to write the amount.
 *  - Footer "Favor cotizar y devolver — ProMedic" + contact + page numbers.
 *
 * The rfq should include items[] and (optionally) project → client.
 *
 * Because the logo is loaded asynchronously, `buildRfqDoc` is async and both
 * public exports await it.
 *
 * @module utils/rfqExport
 */

// Official palette
const PRIMARY = [0, 87, 184]; // #0057B8 Azul Médico primario
const PRIMARY_DARK = [0, 72, 156]; // #00489C
const ACCENT = [247, 147, 30]; // #F7931E Naranja Industrial
const GRAY_BG = [248, 249, 250]; // #f8f9fa
const GRAY_TEXT = [108, 117, 125];
const ALCANCE_TINT = [230, 239, 250]; // light blue tint of PRIMARY
const ROW_ALT = [246, 249, 253]; // very light row shade

/**
 * Format date for PDF rendering (es-CR, day/month/year)
 * @param {string|Date} value
 * @returns {string}
 */
const formatDate = (value) => {
  if (!value) return '-';
  try {
    const dateObj = typeof value === 'string' ? new Date(value) : value;
    const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    return new Intl.DateTimeFormat('es-CR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(isDateOnly && { timeZone: 'UTC' }),
    }).format(dateObj);
  } catch (err) {
    return '-';
  }
};

const COMPANY_INFO = {
  name: 'ProMedic Group',
  address: 'Costa Rica, Santa Ana, Brasil',
  legalId: 'Cedula juridica 3-102-924057',
  phone: '+506 8837-7017',
  email: 'jorge.murillo@promedicgroup.com',
  contactName: 'Jorge Murillo', // contacto focal para dudas de la ferretería
};

/**
 * Instructions shown to the store (ferretería): what to quote, where to send it,
 * and who to validate details with. Falls back to a standard text built from
 * COMPANY_INFO; an rfq can override the whole message via `rfq.store_instructions`.
 * @param {Object} rfq
 * @returns {string}
 */
export const buildStoreInstructions = (rfq = {}) =>
  rfq.store_instructions ||
  `Favor cotizar los productos indicados en este documento y enviar la cotización en ` +
    `formato digital al correo ${COMPANY_INFO.email}. Ante cualquier duda o detalle por ` +
    `confirmar (medidas, especificaciones o cantidades), validarlo con el contacto focal ` +
    `antes de cotizar: ${COMPANY_INFO.contactName} — ${COMPANY_INFO.phone}.`;

/**
 * Load the white ProMedic logo as an <img> element for embedding.
 * Resolves to the image on success, or null if it fails to load (so the caller
 * can fall back to text without breaking the PDF).
 * @returns {Promise<HTMLImageElement|null>}
 */
const loadLogo = () =>
  new Promise((resolve) => {
    try {
      if (typeof Image === 'undefined') {
        resolve(null);
        return;
      }
      let settled = false;
      const done = (val) => {
        if (!settled) {
          settled = true;
          resolve(val);
        }
      };
      const img = new Image();
      img.onload = () => done(img);
      img.onerror = () => done(null);
      // Guard: if neither onload nor onerror ever fires (broken/slow asset, or
      // an environment like jsdom that doesn't load subresources), don't hang
      // PDF generation forever — fall back to the text logo after a short wait.
      setTimeout(() => done(null), 4000);
      img.src = `${process.env.PUBLIC_URL || ''}/images/logos/Logo_PROMEDIC_Blanco-07.png`;
    } catch (err) {
      resolve(null);
    }
  });

/**
 * Group items by `alcance`, preserving first-seen order. Items without an
 * alcance fall into a "Sin alcance" bucket so nothing is silently dropped.
 * @param {Array} items
 * @returns {Array<{ alcance: string, items: Array }>}
 */
const groupByAlcance = (items = []) => {
  const groups = [];
  const index = new Map();
  items.forEach((item) => {
    const key = item.alcance || 'Sin alcance';
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ alcance: key, items: [] });
    }
    groups[index.get(key)].items.push(item);
  });
  return groups;
};

/** Label shown in the "Proyecto:" block when the RFQ has no project. */
export const NO_PROJECT_LABEL = 'Compra general';

/**
 * Resolve the lines for the "Proyecto:" info block.
 * Number + title identify the project; both are shown when they add info, but a
 * line is never repeated — a title equal to the project number or to the client
 * name is redundant (the client already appears in its own block). When there is
 * no project at all, falls back to NO_PROJECT_LABEL (RFQs can be raised before a
 * formal project exists, e.g. a direct purchase for a client).
 * @param {Object} [project] rfq.project
 * @returns {string[]}
 */
export const resolveProjectLines = (project) => {
  const clientName = project?.client?.company_name || '';
  const lines = [];
  if (project?.project_number) lines.push(String(project.project_number));
  if (
    project?.title &&
    project.title !== project.project_number &&
    project.title !== clientName
  ) {
    lines.push(String(project.title));
  }
  if (lines.length === 0) lines.push(NO_PROJECT_LABEL);
  return lines;
};

/**
 * Build the jsPDF document for an RFQ (price-less, branded, grouped by alcance).
 * @param {Object} rfq
 * @param {Object} [options]
 * @param {HTMLImageElement|{data:string,naturalWidth:number,naturalHeight:number}} [options.logo]
 *   Pre-loaded logo, to drive the same builder outside the browser (Node/tests)
 *   where `Image` is unavailable. In the browser this is omitted and the white
 *   logo is loaded from /images automatically. Pass `null` to force the text
 *   fallback. Accepts an <img> (browser) or a `{data, naturalWidth, naturalHeight}`
 *   object where `data` is a base64 data URL.
 * @returns {Promise<import('jspdf').jsPDF>}
 */
export const buildRfqDoc = async (rfq, options = {}) => {
  const { jsPDF } = await import('jspdf');
  const logo =
    Object.prototype.hasOwnProperty.call(options, 'logo') ? options.logo : await loadLogo();

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;

  const BAND_HEIGHT = 38;
  let yPos = margin;

  const drawFooter = () => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      // thin accent line above the footer
      doc.setDrawColor(...ACCENT);
      doc.setLineWidth(0.4);
      doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);

      doc.setFontSize(8);
      doc.setTextColor(...GRAY_TEXT);
      doc.setFont('helvetica', 'italic');
      doc.text('Favor cotizar y devolver — ProMedic', pageWidth / 2, pageHeight - 13, {
        align: 'center',
      });
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${COMPANY_INFO.email}  |  ${COMPANY_INFO.phone}`,
        pageWidth / 2,
        pageHeight - 9,
        { align: 'center' }
      );
      doc.text(
        `Pagina ${i} de ${totalPages}`,
        pageWidth - margin,
        pageHeight - 5,
        { align: 'right' }
      );
    }
  };

  const checkPageBreak = (requiredSpace = 20) => {
    if (yPos + requiredSpace > pageHeight - 24) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // ============ HEADER (blue band + logo + company + title) ============
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, BAND_HEIGHT, 'F');

  // Logo (white) on the left, with text fallback.
  const logoH = 10;
  let logoBottom = 7 + logoH;
  if (logo) {
    let logoW = 28;
    if (logo.naturalWidth && logo.naturalHeight) {
      logoW = logoH * (logo.naturalWidth / logo.naturalHeight);
    }
    try {
      // 'FAST' aplica compresión Flate al embeber el logo: sin esto jsPDF lo
      // guarda como bitmap RGB + máscara alpha sin comprimir (~1.1MB para un
      // logo chico). Con compresión baja a unos pocos KB.
      // logo.data (base64 data URL) cuando viene inyectado desde Node; el <img>
      // del browser se pasa tal cual.
      const imageData = logo.data ?? logo;
      doc.addImage(imageData, 'PNG', margin, 7, logoW, logoH, undefined, 'FAST');
    } catch (err) {
      // If embedding fails at runtime, fall back to the text name.
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(COMPANY_INFO.name, margin, 15);
    }
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(COMPANY_INFO.name, margin, 15);
  }

  // Company info under the logo (white, small).
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  let infoY = logoBottom + 3.5;
  doc.text(COMPANY_INFO.address, margin, infoY);
  infoY += 3.6;
  doc.text(COMPANY_INFO.legalId, margin, infoY);
  infoY += 3.6;
  doc.text(`${COMPANY_INFO.phone}  |  ${COMPANY_INFO.email}`, margin, infoY);

  // RFQ metadata top right (white).
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('SOLICITUD DE COTIZACIÓN', pageWidth - margin, 15, { align: 'right' });

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`No. ${rfq.rfq_number || ''}`, pageWidth - margin, 22, { align: 'right' });
  doc.text(`Fecha: ${formatDate(rfq.created_at || new Date())}`, pageWidth - margin, 27, {
    align: 'right',
  });

  // Orange accent line just below the blue band, full width.
  doc.setFillColor(...ACCENT);
  doc.rect(0, BAND_HEIGHT, pageWidth, 1.2, 'F');

  yPos = BAND_HEIGHT + 1.2 + 7;

  // ============ CLIENT + PROJECT INFO BLOCKS ============
  // #422: con proyecto, el cliente "oficial" es project.client; sin
  // proyecto (compra por excepción), cae al cliente directo rfq.client.
  const client = rfq.project?.client || rfq.client || {};
  const clientLines = [client.company_name || '-'];
  if (client.legal_id) clientLines.push(`Cedula: ${client.legal_id}`);
  if (client.primary_contact_email) clientLines.push(client.primary_contact_email);
  if (client.primary_contact_phone) clientLines.push(client.primary_contact_phone);

  const projectLines = resolveProjectLines(rfq.project);

  const blockGap = 6;
  const blockWidth = (contentWidth - blockGap) / 2;
  const leftX = margin;
  const rightX = margin + blockWidth + blockGap;
  const lineH = 4.4;
  const blockPadTop = 5;
  const blockPadBottom = 3.5;
  const maxLines = Math.max(clientLines.length, projectLines.length);
  const blockHeight = blockPadTop + 4 + maxLines * lineH + blockPadBottom;

  const drawInfoBlock = (x, label, lines) => {
    doc.setFillColor(...GRAY_BG);
    doc.rect(x, yPos, blockWidth, blockHeight, 'F');
    // label in primary blue bold
    doc.setTextColor(...PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text(label, x + 3, yPos + blockPadTop + 1);
    // content in black
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    let ly = yPos + blockPadTop + 6;
    lines.forEach((line) => {
      doc.text(String(line), x + 3, ly);
      ly += lineH;
    });
  };

  drawInfoBlock(leftX, 'Cliente:', clientLines);
  drawInfoBlock(rightX, 'Proyecto:', projectLines);

  yPos += blockHeight + 6;

  // RFQ title (if any), bold.
  if (rfq.title) {
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const titleLines = doc.splitTextToSize(String(rfq.title), contentWidth);
    titleLines.forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 5.5;
    });
    yPos += 1;
  }

  // ============ INSTRUCTIONS FOR THE STORE (callout) ============
  {
    const instrText = buildStoreInstructions(rfq);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const instrLines = doc.splitTextToSize(instrText, contentWidth - 8);
    const boxPadTop = 4.5;
    const boxPadBottom = 3.5;
    const instrLineH = 4.2;
    const boxHeight = boxPadTop + 4.5 + instrLines.length * instrLineH + boxPadBottom;
    checkPageBreak(boxHeight + 4);
    // Light-blue tint panel with an orange left accent bar.
    doc.setFillColor(...ALCANCE_TINT);
    doc.rect(margin, yPos, contentWidth, boxHeight, 'F');
    doc.setFillColor(...ACCENT);
    doc.rect(margin, yPos, 1.5, boxHeight, 'F');
    doc.setTextColor(...PRIMARY_DARK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Instrucciones para la ferretería:', margin + 4, yPos + boxPadTop + 2);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    let iy = yPos + boxPadTop + 7.5;
    instrLines.forEach((line) => {
      doc.text(line, margin + 4, iy);
      iy += instrLineH;
    });
    yPos += boxHeight + 6;
  }

  // ============ COLUMN LAYOUT ============
  // Producto | Descripción | Cant. | Unidad | Precio unit. | Subtotal
  const cols = {
    producto: { x: margin + 1.5, w: contentWidth * 0.26, align: 'left' },
    descripcion: { x: margin + contentWidth * 0.29, w: contentWidth * 0.28, align: 'left' },
    qty: { x: margin + contentWidth * 0.64, align: 'right' },
    unit: { x: margin + contentWidth * 0.66, align: 'left' },
    unitPrice: { x: margin + contentWidth * 0.865, align: 'right' },
    subtotal: { x: margin + contentWidth - 1.5, align: 'right' },
  };

  const drawTableHeader = () => {
    checkPageBreak(14);
    doc.setFillColor(...PRIMARY);
    doc.rect(margin, yPos, contentWidth, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('Producto', cols.producto.x, yPos + 4.8);
    doc.text('Descripcion', cols.descripcion.x, yPos + 4.8);
    doc.text('Cant.', cols.qty.x, yPos + 4.8, { align: 'right' });
    doc.text('Unidad', cols.unit.x, yPos + 4.8);
    doc.text('Precio est. unit.', cols.unitPrice.x, yPos + 4.8, { align: 'right' });
    doc.text('Subtotal', cols.subtotal.x, yPos + 4.8, { align: 'right' });
    yPos += 7;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  };

  // ============ ITEMS GROUPED BY ALCANCE ============
  const groups = groupByAlcance(rfq.items || []);
  let rowIdx = 0;

  groups.forEach((group) => {
    // Section header for the alcance (light blue tint band).
    checkPageBreak(20);
    doc.setFillColor(...ALCANCE_TINT);
    doc.rect(margin, yPos, contentWidth, 6.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...PRIMARY_DARK);
    doc.text(String(group.alcance), margin + 2, yPos + 4.5);
    doc.setTextColor(0, 0, 0);
    yPos += 6.5;

    drawTableHeader();

    group.items.forEach((item) => {
      // Unknown quantity is simply left blank in the Cant. column — no marker.
      const productName = item.name || item.description || '-';
      const productText = String(productName);
      // If name is used as the fallback, don't repeat it in the description column.
      const descriptionText =
        item.name && item.description && item.description !== item.name
          ? String(item.description)
          : '';

      const splitProduct = doc.splitTextToSize(productText, cols.producto.w - 2);
      const splitDescription = descriptionText
        ? doc.splitTextToSize(descriptionText, cols.descripcion.w - 2)
        : [''];
      const lineHeight = 4.4;
      const maxRowLines = Math.max(splitProduct.length, splitDescription.length);
      const rowHeight = Math.max(maxRowLines * lineHeight + 2.5, 7);

      if (checkPageBreak(rowHeight + 5)) {
        drawTableHeader();
      }

      if (rowIdx % 2 === 0) {
        doc.setFillColor(...ROW_ALT);
        doc.rect(margin, yPos, contentWidth, rowHeight, 'F');
      }

      const baseY = yPos + 4.6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(splitProduct, cols.producto.x, baseY);
      if (descriptionText) {
        doc.setTextColor(...GRAY_TEXT);
        doc.text(splitDescription, cols.descripcion.x, baseY);
        doc.setTextColor(0, 0, 0);
      }
      doc.text(String(item.quantity ?? ''), cols.qty.x, baseY, { align: 'right' });
      doc.text(String(item.unit || ''), cols.unit.x, baseY);
      // Precio unit. + Subtotal: intentionally left EMPTY (no prices in an RFQ).

      yPos += rowHeight;
      rowIdx += 1;
    });

    yPos += 3.5;
  });

  // ============ TOTAL GENERAL (accent line to fill) ============
  checkPageBreak(18);
  yPos += 2;
  const totalLabelX = pageWidth - margin - 78;
  const totalRightX = pageWidth - margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PRIMARY_DARK);
  doc.text('Total general:', totalLabelX, yPos);
  doc.setTextColor(0, 0, 0);
  // Orange accent line as the blank space to write the amount.
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(0.6);
  doc.line(totalRightX - 48, yPos + 1.5, totalRightX, yPos + 1.5);
  yPos += 10;

  // ============ NOTES ============
  if (rfq.notes) {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...PRIMARY_DARK);
    doc.text('Notas:', margin, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(String(rfq.notes), contentWidth);
    noteLines.forEach((line) => {
      checkPageBreak(6);
      doc.text(line, margin, yPos);
      yPos += 4.5;
    });
  }

  // Footer for all pages
  drawFooter();

  return doc;
};

/**
 * Export an RFQ to a downloadable PDF.
 * @param {Object} rfq
 * @param {Object} [options] forwarded to buildRfqDoc (e.g. `{ logo }`)
 * @returns {Promise<void>}
 */
export const exportRfqToPDF = async (rfq, options) => {
  if (!rfq) return;
  const doc = await buildRfqDoc(rfq, options);
  doc.save(`solicitud-cotizacion-${rfq.rfq_number || 'sin-numero'}.pdf`);
};

/**
 * Generate an RFQ PDF as a Blob (for the storage snapshot on "marcar enviada").
 * @param {Object} rfq
 * @param {Object} [options] forwarded to buildRfqDoc (e.g. `{ logo }`)
 * @returns {Promise<Blob>}
 */
export const generateRfqPdfBlob = async (rfq, options) => {
  const doc = await buildRfqDoc(rfq, options);
  return doc.output('blob');
};

const rfqExport = {
  buildRfqDoc,
  exportRfqToPDF,
  generateRfqPdfBlob,
};

export default rfqExport;
